import { mediaTypeColorKeyReverseMap } from '@Common/Basic/enums/media-type.enum';
import { ColorPickerFieldComponent } from '@Common/PageBuilder/components/FormFields/ColorPickerField/color-picker-field.component';
import { UserAppPreferenceChartColors } from '../../../Core/Users/models/user-app-preferences-chart-colors.model';
import { AdvancedColorPickerFieldModel } from '@Common/PageBuilder/models/advanced-color-picker-field.model';

type ChartColorPreference<K extends keyof UserAppPreferenceChartColors> = {
    [key in keyof UserAppPreferenceChartColors[K]]: ChartColorValue;
};

type GeneralColors = ChartColorPreference<'generalColors'>;
type MediaTypeColors = ChartColorPreference<'mediaTypeColors'>;
type SentimentColors = ChartColorPreference<'sentimentColors'>;
type BarColors = ChartColorPreference<'barColors'>;
type OtherColors = ChartColorPreference<'otherColors'>;


export class ChartColorValue {
    field: AdvancedColorPickerFieldModel;
    value: string;

    constructor(label: string, value: string) {
        this.field = this.createColorPickerFieldModel(label, value);
        this.value = value;
    }

    updateValue(newValue: string) {
        this.value = newValue;
    };

    private createColorPickerFieldModel(label: string, color: string): AdvancedColorPickerFieldModel {
        return AdvancedColorPickerFieldModel.create({
            fieldComponent: ColorPickerFieldComponent,
            label,
            initialValue: color
        })
    }
}

export class ChartColorPreferenceModel {
    generalColors: GeneralColors;
    mediaTypeColors: MediaTypeColors;
    sentimentColors: SentimentColors;
    barColors: BarColors;
    otherColors: OtherColors;

    constructor(defaultColorPreferences: UserAppPreferenceChartColors) {
        this.generalColors = {} as GeneralColors;
        Object.entries(defaultColorPreferences.generalColors).forEach(([k, v]) => {
            this.generalColors[k] = new ChartColorValue(`Report.userPreferences.defaultColors.${k}`, v);
        });

        this.mediaTypeColors = {} as MediaTypeColors;
        Object.entries(defaultColorPreferences.mediaTypeColors).forEach(([k, v]) => {
            this.mediaTypeColors[k] = new ChartColorValue(`MediaType.${mediaTypeColorKeyReverseMap.get(k)}`, v);
        });

        this.sentimentColors = {} as SentimentColors;
        Object.entries(defaultColorPreferences.sentimentColors).forEach(([k, v]) => {
            this.sentimentColors[k] = new ChartColorValue(`Reports.sentiment.${k}`, v);
        });

        this.barColors = {} as BarColors;
        this.barColors.barColor = new ChartColorValue(
            'Report.userPreferences.defaultColors.bar',
            defaultColorPreferences.barColors.barColor
        );
    }

    public getCurrentValues(): UserAppPreferenceChartColors {
        return {
            generalColors: this.convertToUserPreferencesProperty<'generalColors'>(this.generalColors),
            mediaTypeColors: this.convertToUserPreferencesProperty<'mediaTypeColors'>(this.mediaTypeColors),
            sentimentColors:  this.convertToUserPreferencesProperty<'sentimentColors'>(this.sentimentColors),
            barColors: this.convertToUserPreferencesProperty<'barColors'>(this.barColors),
        };
    }

    /**
     * Maps a ChartColorPreference back to its UserAppPreferencesChartColors equivalent
     */
    private convertToUserPreferencesProperty<K extends keyof UserAppPreferenceChartColors>(
        colors: ChartColorPreference<K>
    ): UserAppPreferenceChartColors[K] {
        return Object.keys(colors).reduce((o, key) => {
            o[key] = colors[key].value;
            return o;
        }, {} as UserAppPreferenceChartColors[K]);
    }
}

